
import { Vue, Component, Prop } from 'vue-property-decorator';
import UsStates from '@/Models/Static/us_states.json';
import CaStates from '@/Models/Static/ca_states.json';
import StripeCheckoutService from '@/Services/Subscription/StripeCheckoutService';
import VeeValidateRefs from '@/Models/VeeValidateRefs';
import DbipLookupService from '@/Services/DbipLookupService';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import store from '@/store';
import { TranslateResult } from 'vue-i18n';
import Alert from '@/components/Alert.vue';
import { debounce } from 'lodash';

@Component({
  components: {
    StateDropdown: () => import('@/components/Input/StateDropdown.vue'),
    GooglePlacesAutocomplete: () => import('@/components/Input/GooglePlacesAutocomplete.vue'),
    ValidationObserver,
    ValidationProvider,
    Alert,
  },
})
export default class BillingAddressEdit extends Vue {
  @Prop(Boolean)
  open!: boolean;

  @Prop({ default: false })
  isPaymentMode?: boolean;

  team = store.state.auth.team;

  showManualAddressInput = !!this.team.line1;

  openCountryModal = false;

  detectedCountry: any;

  showStateDropdown = false;

  statesList: Array<any> = [];

  legalEntityB2B: string = 'b2b';

  showLoading: boolean = false;

  formErrorMessage: TranslateResult = '';

  // Handles business logic and some of the form state
  stripeCheckoutService: StripeCheckoutService = new StripeCheckoutService();

  $refs!: {
    // Used for validation of the form data<ValidationObserver slim ref="observer">
    observer: VeeValidateRefs;
  };

  async update(): Promise<void> {
    this.showLoading = true;
    this.formErrorMessage = '';
    if (await this.$refs.observer.validate()) {
      await store.dispatch('auth/updateTeam', this.team).then(() => {
        this.$emit('cancel');
      });
    } else {
      this.formErrorMessage = this.$t('fields.checkout.form.invalid');
      this.showLoading = false;
    }
    debounce(() => {
      this.formErrorMessage = '';
    }, 3);
  }

  toggleManualAddressInput(toggle: boolean, googlePlacesData: any = null): void {
    this.showManualAddressInput = toggle;

    if (googlePlacesData) {
      if (googlePlacesData.street && googlePlacesData.house) {
        this.team.line1 = googlePlacesData.street + ' ' + googlePlacesData.house;
      } else if (googlePlacesData.street && !googlePlacesData.house) {
        this.team.line1 = googlePlacesData.street;
      }
      this.team.postal_code = googlePlacesData.zip;
      this.team.city = googlePlacesData.city;
    }
  }

  /**
   * Checks if browser supports geolocation-detection
   */
  tryGeolocationDetection(): boolean {
    return 'geolocation' in navigator;
  }

  /**
   * Detects the user's country via geolocation-detection
   */
  detectCountry(): void {
    // If browser supports geolocation-detection
    if (this.tryGeolocationDetection()) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const pos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          const dbipLookupService = new DbipLookupService();
          await dbipLookupService.getLocationViaLatLong(pos.lat, pos.lng).then((result: any) => {
            if (result) {
              this.detectedCountry = result.data.country;
            }
          });

          this.openCountryModal = false;
        },
        function (error) {
          console.log(error);
        }
      );
    }
  }

  // Sets the list of states for specific countries
  setStatesList(country: string): void {
    if (country.toLowerCase() === 'us') {
      this.statesList = UsStates;
      this.showStateDropdown = true;
    } else if (country.toLowerCase() === 'ca') {
      this.statesList = CaStates;
      this.showStateDropdown = true;
    }
  }

  mounted(): void {
    // Set list of states for country
    this.setStatesList(this.stripeCheckoutService.country.country_iso2);
  }
}
